import type { ScheduleCoverageRow } from "./schedule";
import type { ShiftTemplateRow } from "./schedule-templates";

// Publish review preview (CONSOLE-SCHEDULE-PUBLISH-REVIEW-GATE). A PURE projection of what
// `publish_shift_template` will do to the visible week for one template, built from the
// coverage rows the Builder already holds. This module mutates nothing and issues no query,
// so the review gate stays zero-rpc until the supervisor confirms.
//
// The verdicts mirror the shipped RPC (migration 20260611044742) rather than guessing at it.
// That function:
//   effective_start = greatest(window_start, template.active_from)
//   effective_end   = least(window_end, coalesce(template.active_until, window_end))
//   candidates      = every date in that range whose `extract(dow ...)` is in template.weekdays
//   creates a slot unless one already exists with the SAME account, template, slot_date AND
//   starts_at
//
// Two consequences of that last clause are easy to get wrong and are modeled here on purpose:
//
//  1. The match includes `starts_at`, not just the date. If a draft template's start time was
//     edited after slots existed, publishing adds a SECOND slot on the same day rather than
//     moving the first. The review says so instead of reporting "already exists".
//  2. The match ignores slot status. A CANCELLED slot still blocks re-creation, so a
//     supervisor who cancelled a seat and expects publish to bring it back gets told the
//     truth here rather than after the fact.
//  3. `starts_at` is a timestamp, compared for equality. This module therefore compares
//     INSTANTS, never rendered wall clocks: a clock string drops seconds and collapses the
//     two instants of a fall-back hour onto one label, which would let the preview say
//     "already there" about a slot the server is about to create. When the instant cannot be
//     proven - unusable timezone, malformed time, a wall clock that does not exist or
//     happens twice on that date - the row falls back to `create`. The preview is allowed to
//     over-state what publish does; it is never allowed to hide a slot that will go live.

export type PublishVerdict = "create" | "exists";

export interface PublishPreviewRow {
  /** YYYY-MM-DD, in the account's schedule date space (the RPC's own slot_date). */
  date: string;
  /** Sun..Sat, from the Postgres dow convention the template's weekdays use. */
  weekdayLabel: string;
  /** e.g. "22:00 -> 06:00 +1", in the template's site timezone (its own local clock). */
  window: string;
  verdict: PublishVerdict;
  /**
   * `exists` rows only: the slot already there is cancelled. Publish will NOT bring it back,
   * because the server's idempotency check does not look at status.
   */
  existingIsCancelled: boolean;
  /**
   * `create` rows only: a slot for this template already sits on this date at a different
   * time, so this publish adds another one beside it rather than replacing it.
   */
  addsAlongsideExisting: boolean;
  /** Seats this row would put in front of officers if it is created. */
  headcountRequired: number;
}

export interface PublishPreview {
  rows: PublishPreviewRow[];
  summary: {
    createCount: number;
    existsCount: number;
    /** Headcount across created rows: what actually goes live unassigned. */
    openSeatCount: number;
    cancelledBlockingCount: number;
    duplicateTimeCount: number;
  };
  /** The template's active window clipped the visible week to nothing. */
  clippedToNothing: boolean;
  /** Present when the template's active window narrows the visible week. */
  effectiveStart: string | null;
  effectiveEnd: string | null;
}

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

function toUtcDate(dateIso: string): Date {
  const [year, month, day] = dateIso.split("-").map(Number);
  return new Date(Date.UTC(year!, month! - 1, day!));
}

function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Inclusive date range, matching the RPC's `generate_series(..., interval '1 day')`. */
function datesBetween(startIso: string, endIso: string): string[] {
  const out: string[] = [];
  const end = toUtcDate(endIso).getTime();
  for (const cursor = toUtcDate(startIso); cursor.getTime() <= end; cursor.setUTCDate(cursor.getUTCDate() + 1)) {
    out.push(toIsoDate(cursor));
  }
  return out;
}

function maxIso(a: string, b: string): string {
  return a >= b ? a : b;
}

function minIso(a: string, b: string): string {
  return a <= b ? a : b;
}

/** "HH:mm" from a "HH:mm[:ss]" template time. */
function localStartLabel(localStartTime: string): string {
  return localStartTime.slice(0, 5);
}

/** The template's own local window, e.g. "22:00 -> 06:00 +1". Pure clock arithmetic. */
export function templateWindowLabel(localStartTime: string, durationMinutes: number): string {
  const [hours, minutes] = localStartLabel(localStartTime).split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return "time not set";
  }
  const startTotal = hours! * 60 + minutes!;
  const endTotal = startTotal + Math.max(0, durationMinutes);
  const pad = (value: number) => String(value).padStart(2, "0");
  const endClock = `${pad(Math.floor((endTotal % 1440) / 60))}:${pad(endTotal % 60)}`;
  const dayShift = Math.floor(endTotal / 1440);
  return `${localStartLabel(localStartTime)} -> ${endClock}${dayShift > 0 ? ` +${dayShift}` : ""}`;
}

/**
 * Milliseconds to add to an instant to reach the wall clock the site timezone shows for it.
 * Returns null when the zone is unusable, so a malformed `site_timezone` (the column's shape
 * constraint permits strings no IANA database carries) can never throw out of the preview.
 */
function zoneOffsetMs(instantMs: number, timeZone: string): number | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      // Pinned rather than `hour12: false`, which resolves to the h24 cycle on some ICU
      // builds and renders midnight as "24:00".
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).formatToParts(new Date(instantMs));
    const read = (type: string): number => {
      const part = parts.find((candidate) => candidate.type === type);
      return part === undefined ? Number.NaN : Number(part.value);
    };
    const wallMs = Date.UTC(
      read("year"),
      read("month") - 1,
      read("day"),
      read("hour"),
      read("minute"),
      read("second"),
    );
    return Number.isFinite(wallMs) ? wallMs - instantMs : null;
  } catch {
    return null;
  }
}

/** "HH:mm[:ss[.ffffff]]" -> ms since midnight. Sub-millisecond precision is refused, not rounded. */
function timeOfDayMs(localStartTime: string): number | null {
  const parsed = /^(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?$/.exec(localStartTime);
  if (parsed === null) return null;
  const hours = Number(parsed[1]);
  const minutes = Number(parsed[2]);
  const seconds = parsed[3] === undefined ? 0 : Number(parsed[3]);
  if (hours > 24 || minutes > 59 || seconds > 59) return null;
  const micros = Number((parsed[4] ?? "").padEnd(6, "0"));
  if (!Number.isFinite(micros) || micros % 1000 !== 0) return null;
  return ((hours * 60 + minutes) * 60 + seconds) * 1000 + micros / 1000;
}

/** An existing slot's `starts_at` as an instant. Sub-millisecond precision is refused, not rounded. */
function slotInstantMs(startsAt: string): number | null {
  const fraction = /\.(\d+)/.exec(startsAt);
  if (fraction !== null && /[1-9]/.test(fraction[1]!.slice(3))) return null;
  const instantMs = Date.parse(startsAt);
  return Number.isFinite(instantMs) ? instantMs : null;
}

/**
 * The exact instant the server will store for this candidate, i.e. what
 * `(slot_date::timestamp + local_start_time) at time zone site_timezone` evaluates to. The
 * idempotency check compares that timestamp for equality, so the preview compares instants
 * too - a wall-clock comparison would collapse distinct timestamps onto one string and let
 * the preview report "already there" for a row the server is about to create.
 *
 * Returns null whenever the instant cannot be PROVEN: an unusable timezone, a malformed
 * local time, a wall clock that does not exist on that date (spring forward), or one that
 * happens twice (fall back, where the server's choice between the two is not ours to
 * assume). Null makes the row a create, so an unprovable match can only ever over-report
 * what publish does, never hide a slot it will put in front of officers.
 */
function candidateInstantMs(
  dateIso: string,
  localStartTime: string,
  timeZone: string,
): number | null {
  const dayMs = toUtcDate(dateIso).getTime();
  const timeMs = timeOfDayMs(localStartTime);
  if (!Number.isFinite(dayMs) || timeMs === null) return null;
  const wallMs = dayMs + timeMs;

  // Probe a day either side as well as the wall clock itself, so BOTH offsets in play around
  // a transition are found. Probing only at the wall clock would miss the far side of a
  // fall-back hour and make an ambiguous time look settled.
  const probeMs = 26 * 60 * 60 * 1000;
  const offsets = new Set<number>();
  for (const probe of [wallMs - probeMs, wallMs, wallMs + probeMs]) {
    const offset = zoneOffsetMs(probe, timeZone);
    if (offset !== null) offsets.add(offset);
  }
  if (offsets.size === 0) return null;

  // Keep only the instants that render back as exactly the wall clock asked for. Zero of
  // them means the clock does not exist that day; more than one means it happens twice.
  let resolved: number | null = null;
  for (const offset of offsets) {
    const guess = wallMs - offset;
    const actual = zoneOffsetMs(guess, timeZone);
    if (actual === null || guess + actual !== wallMs) continue;
    if (resolved !== null && resolved !== guess) return null;
    resolved = guess;
  }
  return resolved;
}

/**
 * What this publish would do. `coverage` is the visible week's rows; only slots belonging to
 * this template matter, which is why the coverage read carries `shift_template_id`.
 */
export function buildPublishPreview(
  template: ShiftTemplateRow,
  coverage: readonly ScheduleCoverageRow[],
  windowStart: string,
  windowEnd: string,
): PublishPreview {
  const activeFrom = template.activeFrom;
  const effectiveStart = activeFrom !== null ? maxIso(windowStart, activeFrom) : windowStart;
  const effectiveEnd =
    template.activeUntil !== null ? minIso(windowEnd, template.activeUntil) : windowEnd;

  if (effectiveEnd < effectiveStart) {
    return {
      rows: [],
      summary: {
        createCount: 0,
        existsCount: 0,
        openSeatCount: 0,
        cancelledBlockingCount: 0,
        duplicateTimeCount: 0,
      },
      clippedToNothing: true,
      effectiveStart: null,
      effectiveEnd: null,
    };
  }

  const weekdays = new Set(template.weekdays);
  const window = templateWindowLabel(template.localStartTime, template.durationMinutes);

  // Existing slots for THIS template, by date. Cancelled rows are kept: the server's
  // idempotency check does not filter on status, so they still block creation.
  const byDate = new Map<string, ScheduleCoverageRow[]>();
  for (const row of coverage) {
    if (row.shiftTemplateId !== template.templateId || row.slotDate === null) continue;
    const list = byDate.get(row.slotDate);
    if (list === undefined) byDate.set(row.slotDate, [row]);
    else list.push(row);
  }

  const rows: PublishPreviewRow[] = [];
  for (const date of datesBetween(effectiveStart, effectiveEnd)) {
    if (!weekdays.has(toUtcDate(date).getUTCDay())) continue;
    const onDate = byDate.get(date) ?? [];
    const candidate = candidateInstantMs(date, template.localStartTime, template.siteTimezone);
    const sameTime =
      candidate === null
        ? undefined
        : onDate.find((row) => row.startsAt !== null && slotInstantMs(row.startsAt) === candidate);
    rows.push({
      date,
      weekdayLabel: WEEKDAY_SHORT[toUtcDate(date).getUTCDay()] ?? date,
      window,
      verdict: sameTime !== undefined ? "exists" : "create",
      existingIsCancelled: sameTime !== undefined && sameTime.slotStatus === "cancelled",
      addsAlongsideExisting: sameTime === undefined && onDate.length > 0,
      headcountRequired: template.headcountRequired,
    });
  }

  const created = rows.filter((row) => row.verdict === "create");
  return {
    rows,
    summary: {
      createCount: created.length,
      existsCount: rows.length - created.length,
      openSeatCount: created.reduce((total, row) => total + row.headcountRequired, 0),
      cancelledBlockingCount: rows.filter((row) => row.existingIsCancelled).length,
      duplicateTimeCount: created.filter((row) => row.addsAlongsideExisting).length,
    },
    clippedToNothing: false,
    effectiveStart,
    effectiveEnd,
  };
}
